import { isObservable, tap, catchError, throwError, Observable } from "rxjs";
import { electronApi } from "../../../_shared/environment-helper";
import { IPC_CHANNEL } from "../../../_offline/electron.constants";

const LOG_FILE_NAME = "asset-module-migration-logs.txt";
const logLines: string[] = [];
let logFilePath: string | null = null;

type LogConfig = {
  writeArguments?: boolean;
  writeOutput?: boolean;
};

type Event = "args" | "returned" | "emitted" | "error";

const DefaultConfig: LogConfig = {
  writeArguments: true,
  writeOutput: true
};

function writeToFile(line: string): void {
  try {
    if (!logFilePath) {
      const userPath: string = electronApi.sendSync(IPC_CHANNEL.FILE.GET_USER_PATH);
      logFilePath = `${userPath}/${LOG_FILE_NAME}`;
    }
    logLines.push(line);
    electronApi.sendSync(IPC_CHANNEL.FILE.POST_TEXT_FILE, logFilePath, logLines.join("\n"));
  } catch (_) {
    return;
  }
}

function formatLine(tag: string, event: Event, payload: unknown): string {
  const date = new Date().toISOString();
  const body = event === "error" ? String(payload) : JSON.stringify(payload);
  return `[${date}] [${tag}] ${event}: ${body}`;
}

/**
 * @description Logger for the asset module migration. Logs are written in a file "asset-module-migration-logs.txt" in the user data folder.
 * @param label describe the function that would be logged
 * @param config override what would be logged (inputs/outputs)
 * @returns
 */
export function Log(label?: string, config: LogConfig = DefaultConfig) {
  const effectiveConfigs = { ...DefaultConfig, ...config };

  return function (_target: object, propertyKey: string, descriptor: PropertyDescriptor) {
    const original = descriptor.value;
    const tag = label ?? propertyKey;

    descriptor.value = function (...args: unknown[]) {
      if (electronApi && effectiveConfigs.writeArguments) {
        writeToFile(formatLine(tag, "args", args));
      }

      return runFunctionAndLogOutput.call(this, original, args, tag, effectiveConfigs);
    };

    return descriptor;
  };
}

function runFunctionAndLogOutput(original: any, args: unknown[], tag: string, config: LogConfig) {
  let result: unknown;

  try {
    result = original.apply(this, args);
  } catch (err) {
    if (electronApi) {
      writeToFile(formatLine(tag, "error", err));
    }
    throw err;
  }

  if (isObservable(result)) {
    return logObservable(result, tag, config);
  }

  if (result instanceof Promise) {
    return logPromises(result, tag, config);
  }

  if (electronApi && config.writeOutput) {
    writeToFile(formatLine(tag, "returned", result));
  }

  return result;
}

function logObservable(result: Observable<unknown>, tag: string, config: LogConfig) {
  return result.pipe(
    tap((value) => {
      if (electronApi) {
        writeToFile(formatLine(tag, "emitted", value));
      }
    }),
    catchError((err) => {
      if (electronApi && config.writeOutput) {
        writeToFile(formatLine(tag, "error", err));
      }
      return throwError(() => err);
    })
  );
}

function logPromises(result: Promise<unknown>, tag: string, config: LogConfig) {
  return result.then(
    (value: unknown) => {
      if (electronApi && config.writeOutput) {
        writeToFile(formatLine(tag, "returned", value));
      }
      return value;
    },
    (err: unknown) => {
      if (electronApi) {
        writeToFile(formatLine(tag, "error", err));
      }
      return Promise.reject(err);
    }
  );
}
